import { PageContainer } from '@components/PageContainer';
import {
	ChevronIcon,
	InfoIcon,
	LinkItem,
	List,
	LockedIcon,
	PolicyIcon,
	SendIcon,
	SettingsIcon,
	ShieldIcon,
	Typography,
	UserIcon,
} from '@gurupay/gp-ui-library';
import { useFF } from '@hooks/useFF';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CC_FEATURE_FLAGS } from 'types/layout';

interface SettingItem {
	titleKey: string;
	link: string;
	isNewTab?: boolean;
	descriptionKey: string;
	icon: ReactElement;
}

const settingsItems: SettingItem[] = [
	{
		titleKey: 'profileTitle',
		link: '/settings/profile',
		descriptionKey: 'profileDescription',
		icon: <UserIcon />,
	},
	{
		titleKey: 'changePasswordTitle',
		link: import.meta.env.VITE_CHANGE_PASSWORD_LINK,
		descriptionKey: 'changePasswordDescription',
		isNewTab: true,
		icon: <LockedIcon />,
	},
	{
		titleKey: 'legalTitle',
		link: '/settings/legal',
		descriptionKey: 'legalDescription',
		icon: <PolicyIcon />,
	},
	{
		titleKey: 'activityTitle',
		link: '/settings/activity',
		descriptionKey: 'activityDescription',
		icon: <InfoIcon />,
	},
];

const deviceListItem: SettingItem = {
	titleKey: 'pushNotifications',
	link: '/settings/push',
	descriptionKey: 'pushNotificationsDescription',
	icon: <SendIcon />,
};

export const SettingsPage = () => {
	const { t } = useTranslation('', { keyPrefix: 'settings' });
	const hasDeviceList = useFF(CC_FEATURE_FLAGS.DEVICE_LIST);
	const [isCookiebotReady, setIsCookiebotReady] = useState(!!window?.Cookiebot);

	useEffect(() => {
		if (window?.Cookiebot) return;
		const handler = () => setIsCookiebotReady(true);
		window.addEventListener('CookiebotOnLoad', handler);
		return () => window.removeEventListener('CookiebotOnLoad', handler);
	}, []);

	const openCookieBotSettings = () => window.Cookiebot?.renew?.();

	return (
		<PageContainer icon={<SettingsIcon />} title={t('title')}>
			<List className="m-auto max-w-lg">
				{(hasDeviceList ? [...settingsItems, deviceListItem] : settingsItems).map(
					(item) => (
						<LinkItem
							key={item.titleKey}
							description={t(item.descriptionKey)}
							icon={item.icon}
							isNewTab={item.isNewTab}
							link={item.link}
							title={t(item.titleKey)}
						/>
					),
				)}
				{isCookiebotReady && (
					<div
						className="flex cursor-pointer items-center gap-space-16 py-space-16 not-last:border-b border-neutral-100 transition-colors duration-300 px-0 rounded-sm"
						onClick={openCookieBotSettings}
						onKeyDown={(e) =>
							(e.key === 'Enter' || e.key === ' ') && openCookieBotSettings()
						}
						role="button"
						tabIndex={0}
					>
						<span className="p-space-8 bg-neutral-50 rounded-full">
							<ShieldIcon />
						</span>
						<div className="grow">
							<Typography level="h3">{t('cookieTitle')}</Typography>
							<Typography className="text-neutral-600 text-sm-medium" level="text">
								{t('cookieDescription')}
							</Typography>
						</div>
						<ChevronIcon className="rotate-270" width={16} />
					</div>
				)}
			</List>
		</PageContainer>
	);
};
